import { useState, useCallback, useEffect } from 'react';
import { authService } from '../services/authService';

export interface BatchRestoreInfo {
    status: 'deleted' | 'completed' | 'processing' | 'waiting_start' | 'unknown';
    batch_name?: string;
    batch_created_at?: string;
    batch_completed_at?: string;
    total_files?: number;
    processed_files?: number;
    message?: string;
    restorable?: boolean;
    uploads_present?: boolean;
    uploads_count?: number;
    uploads_total_bytes?: number;
}

interface UseBatchRestoreOptions {
    batchStorageKey?: string | null;
    readStoredBatchId: () => string | null;
    setBatchId: (id: string | null) => void;
    onBatchStatusChecked?: (info: BatchRestoreInfo | null) => void;
}

export function useBatchRestore({
    batchStorageKey,
    readStoredBatchId,
    setBatchId,
    onBatchStatusChecked
}: UseBatchRestoreOptions) {
    const [showBatchRestoreModal, setShowBatchRestoreModal] = useState(false);
    const [storedBatchInfo, setStoredBatchInfo] = useState<BatchRestoreInfo | null>(null);
    const [isCheckingBatchStatus, setIsCheckingBatchStatus] = useState(false);

    // Verifica sicura dello stato del batch
    const checkBatchStatus = useCallback(async (batchId: string): Promise<BatchRestoreInfo | null> => {
        try {
            console.log('🔍 Checking batch status:', batchId);
            const statusInfo = await authService.checkBatchStatus(batchId);
            return statusInfo;
        } catch (error) {
            console.error('❌ Error checking batch status:', error);
            return {
                status: 'unknown',
                message: 'Network error during verification'
            };
        }
    }, []);

    // Gestione sicura del ripristino batch con verifica stato
    const handleSecureBatchRestore = useCallback(async () => {
        if (!storedBatchInfo) {
            console.warn('⚠️ No batch information available');
            return;
        }

        const { status } = storedBatchInfo;

        // Solo batch validi possono essere ripristinati
        if (status === 'deleted' || status === 'unknown' || storedBatchInfo.restorable === false) {
            console.warn('⚠️ Batch not restorable:', status);
            clearStoredBatch();
            return;
        }

        console.log('✅ Safe batch restore:', storedBatchInfo.batch_name);
        setShowBatchRestoreModal(false);
        const restoredBatchId = readStoredBatchId();
        setBatchId(restoredBatchId);

        if (restoredBatchId) {
            try {
                const recoveryResult = await authService.recoverStaleBatch(restoredBatchId, {
                    force: false,
                    source: 'restore_batch',
                });
                console.log('♻️ Stale recovery on restore:', recoveryResult);
            } catch (error) {
                // Non bloccare il restore UI: è un tentativo best-effort.
                console.warn('⚠️ Stale recovery call failed during restore:', error);
            }
        }

        if (onBatchStatusChecked) {
            onBatchStatusChecked(storedBatchInfo);
        }
    }, [storedBatchInfo, readStoredBatchId, setBatchId, onBatchStatusChecked]);

    // Pulizia batch dal localStorage
    const clearStoredBatch = useCallback(() => {
        if (batchStorageKey && typeof window !== 'undefined') {
            try {
                localStorage.removeItem(batchStorageKey);
                console.log('🗑️ Batch cleared from localStorage');
            } catch (error) {
                console.error('❌ Error clearing localStorage:', error);
            }
        }
        setStoredBatchInfo(null);
        setShowBatchRestoreModal(false);
        setBatchId(null);
    }, [batchStorageKey, setBatchId]);

    // Chiusura modal senza azione
    const handleCloseBatchRestoreModal = useCallback(() => {
        setShowBatchRestoreModal(false);
    }, []);

    // Gestione ignora (continua senza ripristino) e cancellazione
    const handleIgnoreStoredBatch = useCallback(async () => {
        if (storedBatchInfo && batchStorageKey) {
            const storedBatchId = readStoredBatchId();
            if (storedBatchId) {
                try {
                    console.log('🗑️ Requesting server-side batch deletion:', storedBatchId);
                    await authService.deleteBatchStorage(storedBatchId);
                } catch (error) {
                    console.error('⚠️ Error deleting batch on the server:', error);
                    // Continuiamo comunque con la pulizia locale
                }
            }
        }
        clearStoredBatch();
        console.log('🚫 Previous batch ignored and cleared');
    }, [clearStoredBatch, storedBatchInfo, batchStorageKey, readStoredBatchId]);

    // Effetto per verificare batch salvato quando cambia la chiave di storage
    useEffect(() => {
        if (!batchStorageKey) {
            return;
        }

        const checkStoredBatch = async () => {
            const storedBatchId = readStoredBatchId();

            if (!storedBatchId) {
                return; // Nessun batch salvato
            }

            console.log('📦 Batch found in localStorage:', storedBatchId);
            setIsCheckingBatchStatus(true);

            try {
                const batchStatus = await checkBatchStatus(storedBatchId);

                if (batchStatus) {
                    console.log('📊 Batch status checked:', batchStatus.status);

                    if (batchStatus.uploads_present === false && batchStatus.status !== 'completed') {
                        console.log('🧹 Missing uploads for stored batch, resetting locally');
                        clearStoredBatch();
                        return;
                    }

                    const expiredMessage = 'Session expired: processed files were cleaned up and cannot be restored. Please start a new batch.';
                    const infoToStore = {
                        ...batchStatus,
                        message: batchStatus.message || (batchStatus.restorable === false ? expiredMessage : undefined)
                    };
                    setStoredBatchInfo(infoToStore);

                    const shouldShowModal = (
                        batchStatus.status === 'processing' ||
                        batchStatus.status === 'waiting_start' ||
                        batchStatus.status === 'completed' ||
                        batchStatus.restorable === false
                    );

                    if (shouldShowModal) {
                        if (batchStatus.restorable === false) {
                            setBatchId(null);
                        }
                        setShowBatchRestoreModal(true);
                    }
                }
            } catch (error) {
                console.error('❌ Error while checking batch:', error);
                // In caso di errore, mantieni il batch ma mostra modal di conferma
                setStoredBatchInfo({
                    status: 'unknown',
                    message: 'Unable to verify batch status'
                });
                setShowBatchRestoreModal(true);
            } finally {
                setIsCheckingBatchStatus(false);
            }
        };

        checkStoredBatch();
    }, [batchStorageKey, readStoredBatchId, checkBatchStatus, clearStoredBatch, setBatchId]);

    return {
        // Stati
        showBatchRestoreModal,
        storedBatchInfo,
        isCheckingBatchStatus,

        // Azioni
        handleSecureBatchRestore,
        handleCloseBatchRestoreModal,
        handleIgnoreStoredBatch,
        clearStoredBatch,

        // Utilità
        checkBatchStatus
    };
}
